import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../../../redux';
import { selectModularPolicyId } from '../../../../../redux/slices/ModularEnrolmentSlice/selectors';
import { IPolicy } from '../../../../../redux/slices/PolicyListSlice/types';

type ModularEnrolmentContextType = {
    addOnsModalShown: boolean;
    setAddOnsModalShown: (value: boolean) => void;
    modularAnimationShown: boolean;
    setModularAnimationShown: (value: boolean) => void;
    lastFamilyStructure: string | undefined;
    setLastFamilyStructure: (value: string | undefined) => void;
    isReeditMode: boolean;
    isFamilyInfoUpdated: boolean;
    setIsFamilyInfoUpdated: (value: boolean) => void;
    policy: IPolicy | undefined;
};

const ModularEnrolmentContext = React.createContext<ModularEnrolmentContextType | null>(null);

const useModulerEnrolmentContext = () => {
    const context = React.useContext(ModularEnrolmentContext);
    if (!context) {
        throw new Error('useModulerEnrolmentContext must be used within a ModularEnrolmentProvider');
    }
    return context;
};

export const ModularEnrolmentContextProvider: React.FC = ({ children }) => {
    const rawPolicyList = useSelector((state: ReduxState) => state.policyList.policyList.data) || [];
    const modularPolicyId = useSelector(selectModularPolicyId);
    const [addOnsModalShown, setAddOnsModalShown] = React.useState(false);
    const [modularAnimationShown, setModularAnimationShown] = React.useState(false);
    const [scrolledIntoUpgrade, setScrolledIntoUpgrade] = React.useState(true);
    const [lastFamilyStructure, setLastFamilyStructure] = React.useState<string>();
    const [isFamilyInfoUpdated, setIsFamilyInfoUpdated] = React.useState(false);

    const policy = useMemo(
        () => rawPolicyList.find((p) => p.policyId === modularPolicyId),
        [rawPolicyList, modularPolicyId]
    );
    const isReeditMode = useMemo(() => !!policy?.isCompleted, [policy]);

    const value = useMemo(
        () => ({
            addOnsModalShown,
            setAddOnsModalShown,
            modularAnimationShown,
            setModularAnimationShown,
            scrolledIntoUpgrade,
            setScrolledIntoUpgrade,
            lastFamilyStructure,
            setLastFamilyStructure,
            isReeditMode,
            isFamilyInfoUpdated,
            setIsFamilyInfoUpdated,
            policy
        }),
        [
            addOnsModalShown,
            setAddOnsModalShown,
            modularAnimationShown,
            setModularAnimationShown,
            scrolledIntoUpgrade,
            setScrolledIntoUpgrade,
            lastFamilyStructure,
            setLastFamilyStructure,
            isReeditMode,
            isFamilyInfoUpdated,
            setIsFamilyInfoUpdated,
            policy
        ]
    );

    return <ModularEnrolmentContext.Provider value={value}>{children}</ModularEnrolmentContext.Provider>;
};

export default useModulerEnrolmentContext;
